// Shared by the initial useState and resetQueueState below, so the "empty queue,
// nothing open, nothing selected" shape is defined once.
function getInitialQueueState() {
  return {
    badgeCount: 0,
    open: false,
    // 'idle' | 'loading' | 'loaded' | 'error' — every (re)load of the complete
    // queue (open, retry, after an assign) goes through 'loading', which shows
    // the spinner instead of the previous list.
    loadState: "idle",
    items: [],
    selected: {},
    submitting: false,
    nextStart: 0,
    hasMore: false,
    loadingMore: false,
    // Server-reported total eligible room count -- known even before every
    // page is loaded, so the "select all N rooms" banner can show it.
    total: 0,
    // True after the user opts into "select all N rooms" (beyond what's
    // loaded). No ids are ever enumerated client-side for this -- it's a
    // pure flag; assignSelectedRooms() sends {assignAll: true} and the
    // backend resolves + assigns the current eligible list itself.
    selectAllMode: false,
  };
}

// Page size for both the initial load and each "load more" page.
// Matches the backend's max page size clamp (services/chat.js).
var UNASSIGNED_LINE_GROUPS_PAGE_SIZE = 50;

function UnassignedLineGroupsPanel(props) {
  var lang = props.lang || {};
  var firebaseRef = props.firebaseRef || null;
  var rChatService = props.rChatService || null;
  var toastr = props.toastr || null;
  var watchProfile = props.watchProfile || ""; // channel id for this queue
  var currentUserProfileId = props.currentUserProfileId || "";
  var appId = props.appId || "";
  var assignStatus = props.assignStatus;

  var firebaseWatchRef = React.useRef(null);
  var watchProfileRef = React.useRef(watchProfile);
  var contextKeyRef = React.useRef(
    appId + "|" + watchProfile + "|" + currentUserProfileId,
  );
  var activeRequestRef = React.useRef(0);
  var dialogRef = React.useRef(null);
  var closeButtonRef = React.useRef(null);
  var triggerRef = React.useRef(null);
  var previousFocusRef = React.useRef(null);
  var uiRef = React.useRef(null);
  // Identifies the self-assign request that currently owns `submitting` /
  // the selection. A context reset (channel/business/user switch) bumps it
  // so a late result from the previous context never touches panel state.
  var submitSeqRef = React.useRef(0);
  // Closing the panel only hides the modal (the component stays mounted), but
  // ngReact unmounts it on route change; late promise handlers must not call
  // setState after that.
  var mountedRef = React.useRef(true);

  watchProfileRef.current = watchProfile;
  contextKeyRef.current =
    appId + "|" + watchProfile + "|" + currentUserProfileId;

  var [ui, setUi] = React.useState(getInitialQueueState);
  uiRef.current = ui;

  function getChatId(chat) {
    return (chat && (chat.cid || chat.chat_id || chat.chatId)) || "";
  }

  function areAllSelected(items, selected) {
    return (
      items.length > 0 &&
      items.every(function (item) {
        return !!selected[getChatId(item)];
      })
    );
  }

  function dedupeByChatId(list) {
    var seen = {};

    return list.filter(function (chat) {
      var chatId = getChatId(chat);

      if (!chatId || seen[chatId]) return false;
      seen[chatId] = true;
      return true;
    });
  }

  // True when a response belongs to a superseded request, an unmounted panel
  // or another channel.
  function isStale(requestId, channelId) {
    return (
      !mountedRef.current ||
      requestId !== activeRequestRef.current ||
      channelId !== watchProfileRef.current
    );
  }

  function canEditSelection(state) {
    return !state.submitting && !state.loadingMore && state.loadState === "loaded";
  }

  function notifyError(message) {
    if (toastr && toastr.error) toastr.error(message);
  }

  function getDisplayName(chat) {
    if (!chat) {
      return "";
    }

    return chat.firstname
      ? [chat.firstname, chat.lastname || ""].join(" ").trim()
      : chat.displayname || "";
  }

  function isAssignStatusEnabled() {
    return assignStatus === true || assignStatus === "Y";
  }

  function formatMessage(template, params) {
    var result = template != null ? String(template) : "";

    _.forEach(params || {}, function (value, key) {
      result = result.split("{{" + key + "}}").join(String(value));
    });

    return result;
  }

  function getUnassignedLineGroupErrorCode(error) {
    return (
      _.get(error, "data.msg") ||
      _.get(error, "msg") ||
      _.get(error, "message") ||
      (typeof error === "string" ? error : undefined)
    );
  }

  var ERROR_MESSAGE_KEYS = {
    MISSING_PARAMS: "__chat_assignee_missing_params",
    invalid_channel: "__chat_assignee_invalid_channel",
    team_not_enabled: "__chat_unassigned_line_groups_team_not_enabled",
    // Backend "invalid_chat" = the room no longer exists (a real LINE group cannot stop being one),
    // so users see the same "room is no longer available" reason.
    invalid_chat: "__chat_unassigned_line_groups_chat_unavailable",
    already_assigned: "__chat_unassigned_line_groups_already_assigned",
    chat_unavailable: "__chat_unassigned_line_groups_chat_unavailable",
    GET_UNASSIGNED_LINE_GROUPS_FAILED: "__chat_unassigned_line_groups_load_failed",
    SELF_ASSIGN_FAILED: "__chat_unassigned_line_groups_assign_failed",
  };

  /**
   * Maps a rejected request to a localized, user-safe message. Raw backend or
   * HTTP strings (e.g. "access denied", an HTML error page) are never shown.
   * @param {*} error Rejection value from helper.js (code string, response body, or empty).
   * @param {string} fallbackKey Locale key used when the code is not mapped.
   * @param {string} [fallbackText] English text used when fallbackKey is missing.
   * @returns {string} Localized message.
   */
  function getUnassignedLineGroupErrorMessage(error, fallbackKey, fallbackText) {
    var messageKey = ERROR_MESSAGE_KEYS[getUnassignedLineGroupErrorCode(error)];

    if (messageKey && lang[messageKey]) {
      return lang[messageKey];
    }

    return (
      lang[fallbackKey] ||
      fallbackText ||
      lang.__chat_assign_to_fail ||
      "Something went wrong"
    );
  }

  /**
   * Message for a self-assign request that failed as a whole. A rejection that
   * carries no code at all (network drop, empty HTTP error body) means the
   * outcome of the rooms is unknown.
   * @param {*} error Rejection value from selfAssignLineGroupBulk.
   * @returns {string} Localized message.
   */
  function getAssignRequestErrorMessage(error) {
    var errorCode = getUnassignedLineGroupErrorCode(error);

    if (typeof errorCode !== "string" || !errorCode) {
      return (
        lang.__chat_unassigned_line_groups_outcome_unknown ||
        "Could not confirm the result. The list was refreshed; please check before sending again."
      );
    }

    return getUnassignedLineGroupErrorMessage(
      error,
      "__chat_unassigned_line_groups_assign_failed",
      "Unable to assign rooms",
    );
  }

  function getLoadErrorMessage(error) {
    return getUnassignedLineGroupErrorMessage(
      error,
      "__chat_unassigned_line_groups_load_failed",
      "Unable to load unassigned LINE group rooms",
    );
  }

  /**
   * Shows the outcome of one completed self-assign request. Several rooms: the assigned and
   * failed counts only. A single room that failed: its localized reason (taken by someone
   * else, blocked/archived, no longer a LINE group), so a refused room is never silent.
   * @param {{successes: string[], failures: Array<{chatId: string, msg: string}>, remaining: number}} outcome
   * @returns {void}
   */
  function showAssignOutcomeToasts(outcome) {
    if (!toastr) return;

    var successCount = (outcome.successes || []).length;
    var failures = outcome.failures || [];
    var showWarning = toastr.warning || toastr.error || toastr.info;

    if (failures.length === 0) {
      var showResult = successCount > 0 ? toastr.success : showWarning;

      if (showResult) {
        showResult.call(toastr, formatMessage(
          lang.__chat_unassigned_line_groups_assign_result_success ||
            "{{count}} rooms assigned to you",
          { count: successCount },
        ));
      }
      return;
    }

    if (!showWarning) return;

    if (successCount === 0 && failures.length === 1) {
      showWarning.call(toastr, getUnassignedLineGroupErrorMessage(
        failures[0] && failures[0].msg,
        "__chat_unassigned_line_groups_assign_failed",
        "Unable to assign this LINE group room",
      ));
      return;
    }

    showWarning.call(toastr, formatMessage(
      lang.__chat_unassigned_line_groups_assign_result_partial ||
        "{{success}} rooms assigned, {{failed}} failed",
      { success: successCount, failed: failures.length },
    ));
  }

  // Realtime Firebase membership only drives the trigger badge. While the
  // panel is open the list, selection, total and paging cursor never change
  // by themselves; rooms someone else claimed meanwhile are skipped by the
  // backend at assign time (failures with msg "already_assigned").
  function syncBadgeFromMembership(flags) {
    var badgeCount = Object.keys(flags || {}).length;

    setUi(function (prev) {
      if (prev.badgeCount === badgeCount) return prev;
      return Object.assign({}, prev, { badgeCount: badgeCount });
    });
  }

  function resetQueueState() {
    activeRequestRef.current += 1;
    submitSeqRef.current += 1;

    setUi(function (prev) {
      return Object.assign({}, prev, getInitialQueueState(), {
        badgeCount: prev.badgeCount,
      });
    });
  }

  function loadUnassignedLineGroups() {
    var capturedChannelId = watchProfileRef.current;

    if (
      !capturedChannelId ||
      !rChatService ||
      !rChatService.getUnassignedLineGroups
    ) {
      return Promise.resolve();
    }

    var requestId = ++activeRequestRef.current;

    setUi(function (prev) {
      return Object.assign({}, prev, {
        open: true,
        loadState: "loading",
      });
    });

    return rChatService
      .getUnassignedLineGroups(capturedChannelId, 0, UNASSIGNED_LINE_GROUPS_PAGE_SIZE)
      .then(function (response) {
        if (isStale(requestId, capturedChannelId)) {
          return;
        }

        var rawItems = (response && response.data) || [];
        var total = (response && response.total) || 0;
        var items = dedupeByChatId(rawItems);

        setUi(function (prev) {
          var nextSelected = _.pick(prev.selected, _.map(items, getChatId));

          return Object.assign({}, prev, {
            items: items,
            loadState: "loaded",
            selected: nextSelected,
            // Cursor tracks the raw (pre-dedup) row count so it lines up with
            // the backend's own OFFSET on the next "load more" page.
            nextStart: rawItems.length,
            hasMore: rawItems.length < total,
            total: total,
          });
        });
      })
      .catch(function (error) {
        if (isStale(requestId, capturedChannelId)) {
          return;
        }

        setUi(function (prev) {
          return Object.assign({}, prev, {
            loadState: "error",
            items: [],
            selected: {},
          });
        });

        notifyError(getLoadErrorMessage(error));
      });
  }

  function loadMoreUnassignedLineGroups() {
    if (
      ui.submitting ||
      ui.loadingMore ||
      !ui.hasMore ||
      ui.loadState !== "loaded"
    ) {
      return;
    }

    var capturedChannelId = watchProfileRef.current;

    if (
      !capturedChannelId ||
      !rChatService ||
      !rChatService.getUnassignedLineGroups
    ) {
      return;
    }

    var requestId = ++activeRequestRef.current;
    var startAt = ui.nextStart;

    setUi(function (prev) {
      return Object.assign({}, prev, { loadingMore: true });
    });

    rChatService
      .getUnassignedLineGroups(
        capturedChannelId,
        startAt,
        UNASSIGNED_LINE_GROUPS_PAGE_SIZE,
      )
      .then(function (response) {
        if (isStale(requestId, capturedChannelId)) {
          return;
        }

        var rawItems = (response && response.data) || [];
        var total = (response && response.total) || 0;

        setUi(function (prev) {
          var items = dedupeByChatId(prev.items.concat(rawItems));

          return Object.assign({}, prev, {
            items: items,
            loadingMore: false,
            nextStart: startAt + rawItems.length,
            hasMore: startAt + rawItems.length < total,
            total: total,
          });
        });
      })
      .catch(function (error) {
        if (isStale(requestId, capturedChannelId)) {
          return;
        }

        setUi(function (prev) {
          return Object.assign({}, prev, { loadingMore: false });
        });

        notifyError(getLoadErrorMessage(error));
      });
  }

  // X, Esc and the backdrop may close the panel while a self-assign request
  // is in flight (Cancel is disabled then): the request keeps running and
  // its toasts still appear after the modal is hidden.
  function closePanel() {
    setUi(function (prev) {
      return Object.assign({}, prev, { open: false });
    });
  }

  React.useEffect(function () {
    if (!ui.open) return;

    previousFocusRef.current = document.activeElement;
    if (closeButtonRef.current) closeButtonRef.current.focus();

    return function () {
      if (previousFocusRef.current && previousFocusRef.current.isConnected) {
        previousFocusRef.current.focus();
      } else if (triggerRef.current) {
        triggerRef.current.focus();
      } else {
        var previousTabIndex = document.body.getAttribute("tabindex");
        document.body.setAttribute("tabindex", "-1");
        document.body.focus();
        if (previousTabIndex === null) document.body.removeAttribute("tabindex");
        else document.body.setAttribute("tabindex", previousTabIndex);
      }
    };
  }, [ui.open]);

  function onDialogKeyDown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      closePanel();
      return;
    }
    if (event.key !== "Tab" || !dialogRef.current) return;

    var focusable = dialogRef.current.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), a[href]'
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function toggleSelected(chatId) {
    if (!chatId) {
      return;
    }

    setUi(function (prev) {
      if (!canEditSelection(prev)) {
        return prev;
      }

      // Gmail-style: unchecking one room while "select all" is active drops
      // out of that mode into a concrete selection -- everything currently
      // loaded, minus the room just unchecked.
      if (prev.selectAllMode) {
        var allExceptThis = {};

        prev.items.forEach(function (item) {
          var id = getChatId(item);

          if (id && id !== chatId) {
            allExceptThis[id] = true;
          }
        });

        return Object.assign({}, prev, {
          selectAllMode: false,
          selected: allExceptThis,
        });
      }

      var nextSelected = Object.assign({}, prev.selected);

      if (nextSelected[chatId]) {
        delete nextSelected[chatId];
      } else {
        nextSelected[chatId] = true;
      }

      return Object.assign({}, prev, { selected: nextSelected });
    });
  }

  // Opt-in to "all N rooms" (beyond what's loaded): no ids are fetched or
  // enumerated here at all -- just flip the flag. assignSelectedRooms() checks
  // it and sends {assignAll: true} to the backend, which resolves + assigns
  // the current eligible list itself. Individually-loaded rows are cleared
  // from `selected` since selection is now represented by the mode, not ids.
  // Pressing it again clears the selection.
  function toggleSelectAllMatching() {
    setUi(function (prev) {
      if (!canEditSelection(prev)) {
        return prev;
      }
      if (prev.selectAllMode) {
        return Object.assign({}, prev, { selectAllMode: false, selected: {} });
      }
      return Object.assign({}, prev, { selectAllMode: true, selected: {} });
    });
  }

  // Selects only the rooms currently loaded (explicit ids); pressing it again
  // while they are all selected clears the selection. Never fetches more.
  function selectLoadedRooms() {
    setUi(function (prev) {
      if (!canEditSelection(prev) || !prev.items.length) {
        return prev;
      }

      if (!prev.selectAllMode && areAllSelected(prev.items, prev.selected)) {
        return Object.assign({}, prev, { selected: {} });
      }

      var nextSelected = {};

      prev.items.forEach(function (item) {
        var chatId = getChatId(item);

        if (chatId) {
          nextSelected[chatId] = true;
        }
      });

      return Object.assign({}, prev, {
        selectAllMode: false,
        selected: nextSelected,
      });
    });
  }

  // Clears the finished request's selection so the next "Assign to me" press
  // always starts from a fresh, server-confirmed list.
  function getClearedSubmitState() {
    return {
      submitting: false,
      selected: {},
      selectAllMode: false,
    };
  }

  /**
   * Runs one self-assign request and applies the same post-send rules for the
   * explicit-selection and "select all N rooms" paths: toast the outcome,
   * clear the selection, and reload the list from page 1. No automatic retry.
   * @param {function(): Promise} issueRequest Starts the helper.js request.
   * @returns {Promise} Settles after the outcome has been handled; never rejects
   *   for a request failure (that is reported as a toast instead).
   */
  function sendAssignRequest(issueRequest) {
    // Ignore a prior background refresh once an assignment starts.
    activeRequestRef.current += 1;
    var submitSeq = ++submitSeqRef.current;
    var submitContextKey = contextKeyRef.current;

    setUi(function (prev) {
      return Object.assign({}, prev, {
        submitting: true,
      });
    });

    // Two-argument then(): an exception thrown while handling a successful
    // outcome must not be reported as a whole-request failure.
    return issueRequest().then(
      function (outcome) {
        // Stale context (channel/business/user switched): discard entirely.
        // The context-reset effect already released `submitting`.
        if (submitContextKey !== contextKeyRef.current) return;

        showAssignOutcomeToasts(outcome || {});
        finishAssignRequest(submitSeq);
      },
      function (error) {
        if (submitContextKey !== contextKeyRef.current) return;

        // Whole-request failure: a mapped reason (e.g. team_not_enabled) or,
        // with no response at all, "outcome unknown". Either way the list is
        // reloaded so the user can check before sending again.
        notifyError(getAssignRequestErrorMessage(error));
        finishAssignRequest(submitSeq);
      },
    );
  }

  // Toasts are already shown by the caller, even if the panel was closed or
  // unmounted meanwhile. After every send the selection is cleared and the
  // open list reloaded from page 1 (also when the panel was reopened
  // mid-send). Skipped when unmounted or when a context reset superseded
  // this request. A closed panel skips the reload: openPanel() loads fresh.
  function finishAssignRequest(submitSeq) {
    if (!mountedRef.current || submitSeq !== submitSeqRef.current) return;

    setUi(function (prev) {
      return Object.assign({}, prev, getClearedSubmitState());
    });

    // Full reload: the list is replaced by the spinner until page 1 lands.
    if (uiRef.current.open) {
      loadUnassignedLineGroups();
    }
  }

  // "Select all N rooms" mode: one request, no client-enumerated ids at all
  // (see helper.js#selfAssignLineGroupBulk assignAll / backend assignAll). The backend
  // handles one capped batch and returns how many rooms were not attempted.
  function assignAllRooms() {
    var submitChannelId = watchProfileRef.current;

    return sendAssignRequest(function () {
      return rChatService.selfAssignLineGroupBulk(null, submitChannelId, true);
    });
  }

  function assignSelectedRooms() {
    if (!canEditSelection(ui)) {
      return;
    }

    if (ui.selectAllMode) {
      return assignAllRooms();
    }

    var selectedIds = Object.keys(ui.selected);

    if (!selectedIds.length) {
      return;
    }

    var submitChannelId = watchProfileRef.current;

    // The whole selection goes out in ONE request; the backend claims the
    // rooms in parallel and processes at most a capped number of ids --
    // `remaining` says how many it did not reach.
    return sendAssignRequest(function () {
      return rChatService.selfAssignLineGroupBulk(selectedIds, submitChannelId);
    });
  }

  function retryLoad() {
    loadUnassignedLineGroups();
  }

  function openPanel() {
    // Reopened while a self-assign request is still running (closed
    // mid-send): show that in-flight state as-is -- Assign/Cancel stay
    // disabled -- and let finishAssignRequest() clear the selection and
    // reload from page 1 when the request lands.
    if (uiRef.current.submitting) {
      setUi(function (prev) {
        return Object.assign({}, prev, { open: true });
      });
      return;
    }

    // Nothing in flight: every open starts with no selection.
    setUi(function (prev) {
      return Object.assign({}, prev, getClearedSubmitState());
    });

    // Always a full load with the spinner -- keeping last time's stale list on
    // screen until the new response swapped it in read as a bug.
    loadUnassignedLineGroups();
  }

  React.useEffect(function () {
    mountedRef.current = true;
    return function () {
      mountedRef.current = false;
    };
  }, []);

  var contextIdentityRef = React.useRef(null);

  React.useEffect(
    function () {
      var nextIdentity =
        appId + "|" + watchProfile + "|" + currentUserProfileId;

      if (
        contextIdentityRef.current !== null &&
        contextIdentityRef.current !== nextIdentity
      ) {
        resetQueueState();
      }

      contextIdentityRef.current = nextIdentity;
    },
    [appId, watchProfile, currentUserProfileId],
  );

  React.useEffect(
    function () {
      if (firebaseWatchRef.current) {
        firebaseWatchRef.current.off();
        firebaseWatchRef.current = null;
      }

      if (!firebaseRef || !appId || !watchProfile || !currentUserProfileId) {
        syncBadgeFromMembership({});
        return;
      }

      firebaseWatchRef.current = firebaseRef
        .child(appId)
        .child("unassigned_line_group_reads")
        .child(watchProfile);

      firebaseWatchRef.current.on("value", function (snap) {
        var allData = snap.val() || {};
        var nextFlags = {};

        _.forEach(allData, function (chatData, chatId) {
          if (chatData && chatData.time) {
            nextFlags[chatId] = { time: chatData.time };
          }
        });

        syncBadgeFromMembership(nextFlags);
      });

      return function () {
        if (firebaseWatchRef.current) {
          firebaseWatchRef.current.off();
          firebaseWatchRef.current = null;
        }
      };
      // eslint-disable-next-line
    },
    [firebaseRef, appId, watchProfile, currentUserProfileId],
  );

  function renderTrigger() {
    if (!isAssignStatusEnabled() || !watchProfile || !ui.badgeCount) {
      return null;
    }

    var ariaLabel = formatMessage(
      lang.__chat_unassigned_line_groups_aria_label ||
        "Unassigned LINE groups, {{count}} rooms",
      { count: ui.badgeCount },
    );

    return (
      <div className="warning-notification -line-group">
        <button
          ref={triggerRef}
          className={
            "warning-notification-trigger" + (ui.open ? " -active" : "")
          }
          onClick={function (event) {
            event.stopPropagation();
            openPanel();
          }}
          title={
            lang.__chat_unassigned_line_groups_title || "Unassigned LINE groups"
          }
          aria-label={ariaLabel}
        >
          <i
            className="icon-line-group"
            style={{ fontSize: "20px", color: "#6DA7FD" }}
          ></i>
          {/* Keep the trigger's accessible name current while the dialog is open. */}
          {ui.badgeCount && !ui.open ? (
            <span className="warning-notification-count">
              {ui.badgeCount > 99
                ? "99+"
                : Number(ui.badgeCount).toLocaleString()}
            </span>
          ) : null}
        </button>
      </div>
    );
  }

  function renderBody() {
    if (ui.loadState === "loading") {
      return (
        <div className="sp-placeholder">
          <div className="sp-text-help">
            <i className="icon icon-circle-notch animate-spin"></i>
          </div>
        </div>
      );
    }

    if (ui.loadState === "error") {
      return (
        <div className="sp-placeholder">
          <div className="sp-text-help">
            {lang.__chat_unassigned_line_groups_load_failed ||
              "Unable to load unassigned LINE group rooms"}
          </div>
          <button
            type="button"
            className="sp-btn -border"
            onClick={retryLoad}
            style={{ marginTop: "10px" }}
          >
            {lang.__chat_unassigned_line_groups_retry || "Retry"}
          </button>
        </div>
      );
    }

    return (
      <React.Fragment>
        {!ui.items.length ? (
          <div
            className="sp-text-help"
            style={{ textAlign: "center", padding: "24px 12px" }}
          >
            {lang.__chat_unassigned_line_groups_empty ||
              "No unassigned LINE groups"}
          </div>
        ) : (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "10px" }}
          >
            {ui.items.map(function (chat) {
              var chatId = getChatId(chat);
              var isSelected = ui.selectAllMode || !!ui.selected[chatId];
              // Rows stay clickable during "select all N rooms" mode --
              // unchecking one (toggleSelected) drops out of that mode into
              // a concrete selection of everything loaded except this room.
              var rowDisabled = ui.submitting || ui.loadingMore;

              return (
                <div
                  key={chatId}
                  className="sp-row -gap"
                  style={{
                    padding: "12px",
                    border: "1px solid " + (isSelected ? "#6DA7FD" : "#E5ECF6"),
                    borderRadius: "12px",
                    alignItems: "center",
                    cursor: rowDisabled ? "default" : "pointer",
                    backgroundColor: isSelected ? "#F1F6FF" : "transparent",
                  }}
                  onClick={function () {
                    if (!rowDisabled) {
                      toggleSelected(chatId);
                    }
                  }}
                >
                  <div className="sp-col -wrap">
                    <input
                      type="checkbox"
                      style={{ margin: 0 }}
                      checked={isSelected}
                      aria-label={formatMessage(
                        lang.__chat_unassigned_line_groups_select_room ||
                          "Select {{name}}",
                        { name: getDisplayName(chat) || chat.chat_id || chat.cid },
                      )}
                      disabled={rowDisabled}
                      onClick={function (event) {
                        event.stopPropagation();
                      }}
                      onChange={function () {
                        toggleSelected(chatId);
                      }}
                    />
                  </div>
                  <div className="sp-col -wrap">
                    <img
                      className="sp-logo -md"
                      src={chat.img_profile || "../public/img/team.png"}
                      alt="Chat room"
                    />
                  </div>
                  <div className="sp-col" style={{ minWidth: 0 }}>
                    <div
                      className="sp-text -ellipsis"
                      title={getDisplayName(chat) || chat.chat_id || chat.cid}
                    >
                      {getDisplayName(chat) || chat.chat_id || chat.cid}
                      {chat.line_group_member_count ? (
                        <span> ({chat.line_group_member_count})</span>
                      ) : null}
                    </div>
                    {chat.social_name ? (
                      <div
                        className="sp-text-help -ellipsis"
                        style={{
                          fontSize: "small",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <img
                          src="../public/img/LINE_SOCIAL_Basic.png"
                          style={{
                            width: "14px",
                            height: "14px",
                            flexShrink: 0,
                          }}
                        />
                        {chat.social_name}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}

            {ui.hasMore ? (
              <div style={{ textAlign: "center", padding: "4px 0" }}>
                {ui.loadingMore ? (
                  <i className="icon icon-circle-notch animate-spin"></i>
                ) : (
                  <button
                    type="button"
                    className="sp-btn -border"
                    disabled={ui.submitting}
                    onClick={loadMoreUnassignedLineGroups}
                  >
                    {lang.__chat_unassigned_line_groups_load_more ||
                      "Load more"}
                  </button>
                )}
              </div>
            ) : null}
          </div>
        )}
      </React.Fragment>
    );
  }

  function renderSelectButton(label, isActive, onClick) {
    return (
      <button
        type="button"
        className="sp-btn -border"
        aria-pressed={isActive}
        disabled={ui.submitting || ui.loadingMore}
        onClick={onClick}
      >
        {label}
      </button>
    );
  }

  function renderSubheader() {
    if (ui.loadState !== "loaded" || !ui.items.length) {
      return null;
    }

    var selectedCount = ui.selectAllMode
      ? ui.total
      : Object.keys(ui.selected).length;
    var loadedAllSelected =
      !ui.selectAllMode && areAllSelected(ui.items, ui.selected);

    return (
      <div
        className="sp-row -align-middle"
        style={{
          padding: "10px 12px",
          borderBottom: "1px solid #E5ECF6",
          background: "#F8FAFD",
          flexShrink: 0,
        }}
      >
        <div
          className="sp-col"
          style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
        >
          {ui.hasMore ? (
            <React.Fragment>
              {renderSelectButton(
                lang.__chat_unassigned_line_groups_select_all ||
                  "Select shown rooms",
                loadedAllSelected,
                selectLoadedRooms,
              )}
              {renderSelectButton(
                formatMessage(
                  lang.__chat_unassigned_line_groups_select_all_matching ||
                    "Select all {{total}} rooms",
                  { total: ui.total },
                ),
                ui.selectAllMode,
                toggleSelectAllMatching,
              )}
            </React.Fragment>
          ) : (
            // Everything is loaded: one button, explicit ids (no assignAll).
            renderSelectButton(
              formatMessage(
                lang.__chat_unassigned_line_groups_select_all_matching ||
                  "Select all {{total}} rooms",
                { total: ui.items.length },
              ),
              loadedAllSelected,
              selectLoadedRooms,
            )
          )}
        </div>
        <div className="sp-col -wrap" style={{ textAlign: "right" }}>
          <span
            style={{
              display: "inline-block",
              padding: "4px 10px",
              borderRadius: "12px",
              background: "#F1F6FF",
              color: "#5B6576",
              fontSize: "13px",
              whiteSpace: "nowrap",
            }}
          >
            {formatMessage(
              lang.__chat_unassigned_line_groups_selected_count ||
                "{{count}} selected",
              { count: selectedCount },
            )}
          </span>
        </div>
      </div>
    );
  }

  function renderFooter() {
    if (ui.loadState !== "loaded") {
      return null;
    }

    var selectedCount = ui.selectAllMode
      ? ui.total
      : Object.keys(ui.selected).length;

    return (
      <div
        className="footer sp-row -align-middle"
        style={{
          padding: "12px",
          borderTop: "1px solid #E5ECF6",
          flexShrink: 0,
        }}
      >
        <div
          className="sp-col -wrap"
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "flex-end",
            gap: "8px",
            width: "100%",
          }}
        >
          <button
            type="button"
            className="sp-btn -border"
            disabled={ui.submitting}
            onClick={closePanel}
          >
            {lang.__cancel || "Cancel"}
          </button>
          <button
            type="button"
            className="sp-btn -blue"
            aria-label={lang.__chat_unassigned_line_groups_assign_to_me || "Assign to me"}
            aria-busy={ui.submitting}
            disabled={
              ui.submitting || ui.loadingMore || !selectedCount
            }
            onClick={assignSelectedRooms}
          >
            {ui.submitting ? (
              <i className="icon icon-circle-notch animate-spin" aria-hidden="true"></i>
            ) : lang.__chat_unassigned_line_groups_assign_to_me || "Assign to me"}
          </button>
        </div>
      </div>
    );
  }

  function renderModal() {
    if (!ui.open) {
      return null;
    }

    return (
      <div
        className="warning-notification-backdrop"
        onClick={closePanel}
        onKeyDown={onDialogKeyDown}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "16px",
          background: "rgba(15, 23, 42, 0.22)",
        }}
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="unassigned-line-groups-title"
          className="sp-panel"
          onClick={function (event) {
            event.stopPropagation();
          }}
          style={{
            width: "min(720px, 100%)",
            // Keeps the modal a consistent size while loading (the loading
            // state alone has almost no content, so without this it
            // collapsed to a tiny box with no visible footer).
            minHeight: "min(80vh, 480px)",
            maxHeight: "min(80vh, 720px)",
            overflow: "hidden",
            borderRadius: "16px",
          }}
        >
          <div
            className="header sp-row -align-middle"
            style={{ flexShrink: 0 }}
          >
            <div className="sp-col">
              <div className="sp-label" id="unassigned-line-groups-title">
                {lang.__chat_unassigned_line_groups_title ||
                  "Unassigned LINE groups"}
              </div>
              <div className="sp-text-help">
                {ui.loadState === "loaded"
                  ? lang.__chat_unassigned_line_groups_subtitle ||
                    "Select the chat rooms you want assigned to you from this list."
                  : ""}
              </div>
            </div>
            <button
              ref={closeButtonRef}
              type="button"
              className="sp-col -wrap -clickable"
              aria-label={lang.__close || "Close"}
              onClick={closePanel}
              style={{
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                border: "none",
                background: "transparent",
              }}
            >
              <i
                className="icon icon-cancel"
                style={{ color: "#AEBAD0", fontSize: "16px" }}
              ></i>
            </button>
          </div>

          {renderSubheader()}

          <div
            className="body -scrollable"
            style={{
              padding: "12px",
              overflowY: "auto",
              flex: "1 1 auto",
              // .sp-placeholder (loading/error state) positions itself
              // absolutely against the nearest positioned ancestor -- without
              // this it would escape to .sp-panel and overlap the header.
              position: "relative",
            }}
          >
            {renderBody()}
          </div>

          {renderFooter()}
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      {renderTrigger()}
      {renderModal()}
    </React.Fragment>
  );
}

if (typeof module !== "undefined" && module.exports) {
  module.exports = UnassignedLineGroupsPanel;
}
